import { useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, IconButton, Row } from "@narmi/design_system";
import { useEffect, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { AnnualMonthlyIncome } from "@lending/components/AnnualMonthlyIncome/AnnualMonthlyIncome";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { Card } from "@lending/components/Card/Card";
import { Combobox } from "@lending/components/ComboBox/Combobox";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { PlainButton } from "@lending/components/PlainButton/PlainButton";
import { useCreateApplicantIncomeDetailsQuery } from "@lending/hooks/queries/ApplicantIncomeDetails/useCreateApplicantIncomeDetailsQuery";
import { useDeleteApplicantIncomeDetailsQuery } from "@lending/hooks/queries/ApplicantIncomeDetails/useDeleteApplicantIncomeDetailsQuery";
import { useListApplicantIncomeDetailsQuery } from "@lending/hooks/queries/ApplicantIncomeDetails/useListApplicantIncomeDetailsQuery";
import { usePatchApplicantIncomeDetailsQuery } from "@lending/hooks/queries/ApplicantIncomeDetails/usePatchApplicantIncomeDetailsQuery";
import { useGetCoApplicantQuery } from "@lending/hooks/queries/useGetCoApplicantQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useLendingApplicationSteps } from "@lending/hooks/useLendingApplicationSteps";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import styles from "./EmploymentDetails.module.css";
import { useStore } from "../../../contexts/StoreContext";
import type { ReactLocalization } from "@fluent/react";

function createSchema(l10n: ReactLocalization) {
  return z.object({
    amount: z
      .union([
        z.string().transform((val) => {
          if (val === "$" || val === "") return 0;
          const numericValue = parseFloat(val.replace(/[$,]/g, ""));
          return isNaN(numericValue) ? 0 : numericValue;
        }),
        z.number(),
      ])
      .refine((val) => val > 0, {
        message: l10n.getString("error-required", null, "Required"),
      }),
  });
}

type FormData = z.infer<ReturnType<typeof createSchema>>;
type ApplicantType = "primary" | "co-applicant";
type IncomeDetailsResponse = NonNullable<
  ReturnType<typeof useListApplicantIncomeDetailsQuery>["data"]
>;
type IncomeDetails = IncomeDetailsResponse["results"][number];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function belongsToApplicant(
  income: IncomeDetails,
  applicantType: ApplicantType,
  coApplicantUuid?: string
) {
  const isCoApplicantRow = income.lending_application_applicant_details === coApplicantUuid;
  return applicantType === "co-applicant" ? isCoApplicantRow : !isCoApplicantRow;
}

function getPrimaryIncome({
  applicantType,
  incomeDetailsList,
  coApplicantUuid,
}: {
  applicantType: ApplicantType;
  incomeDetailsList: IncomeDetails[];
  coApplicantUuid?: string;
}) {
  return (
    incomeDetailsList.find(
      (income) => income.is_primary && belongsToApplicant(income, applicantType, coApplicantUuid)
    ) ?? null
  );
}

function getAdditionalIncomes({
  applicantType,
  incomeDetailsList,
  coApplicantUuid,
}: {
  applicantType: ApplicantType;
  incomeDetailsList: IncomeDetails[];
  coApplicantUuid?: string;
}) {
  return incomeDetailsList.filter(
    (income) => !income.is_primary && belongsToApplicant(income, applicantType, coApplicantUuid)
  );
}

function formatEmploymentSummary(income: IncomeDetails, l10n: ReactLocalization) {
  const employer = income.employer_name?.trim();
  const jobTitle = income.job_title?.trim();

  // Retirement takes precedence over the employer name: the model still permits one on a
  // retirement row, so prefilled or legacy data can carry a stale employer.
  let heading: string;
  if (income.income_type === "retirement") {
    heading = l10n.getString("employment-summary-heading-retirement", null, "Retirement income");
  } else if (employer) {
    heading = l10n.getString(
      "employment-summary-heading-with-employer",
      { employer },
      `Income from ${employer}`
    );
  } else {
    heading = l10n.getString("employment-summary-heading-generic", null, "Employment income");
  }

  const startDateParts: string[] = [];
  if (jobTitle) {
    startDateParts.push(jobTitle);
  }
  if (income.employment_start_month && income.employment_start_year) {
    const monthName = MONTH_NAMES[income.employment_start_month - 1] ?? "";
    startDateParts.push(
      l10n.getString(
        "employment-summary-since-date",
        { month: monthName, year: String(income.employment_start_year) },
        `Since ${monthName} ${income.employment_start_year}`
      )
    );
  }

  return { heading, detail: startDateParts.join(" • ") };
}

function parseAmountFromForm(value: unknown): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const numeric = value.replace(/[$,]/g, "");
    if (!numeric) return 0;
    const parsed = parseInt(numeric, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

interface AdditionalIncomeFormData {
  additional_income_type: string;
  amount: number;
}

interface AdditionalIncomeCardProps {
  source: IncomeDetails;
  incomeTypes: string[];
  onIncomeTypeChange: (nextType: string) => void;
  onAmountChange: (nextAmount: number) => void;
  onRemove: () => void;
}

function AdditionalIncomeCard({
  source,
  incomeTypes,
  onIncomeTypeChange,
  onAmountChange,
  onRemove,
}: AdditionalIncomeCardProps) {
  const { l10n } = useLocalization();
  const items = incomeTypes.map((type) => ({ code: type, name: type }));
  const lastSyncedAmount = useRef<number>(source.amount ?? 0);
  const lastSyncedIncomeType = useRef<string>(source.additional_income_type ?? "");

  const { control } = useForm<AdditionalIncomeFormData>({
    defaultValues: {
      additional_income_type: source.additional_income_type ?? "",
      amount: source.amount ?? 0,
    },
  });

  const watchedIncomeType = useWatch({ control, name: "additional_income_type" });
  const watchedAmount = useWatch({ control, name: "amount" });

  useEffect(() => {
    if (typeof watchedIncomeType !== "string") return;
    if (watchedIncomeType === lastSyncedIncomeType.current) return;
    if (!watchedIncomeType) return;
    // Only persist once the value matches an FI-configured type. `useWatch` fires on
    // every intermediate combobox keystroke; without this guard we'd PATCH partial
    // strings and hit server-side validation errors on each one.
    if (!incomeTypes.includes(watchedIncomeType)) return;
    lastSyncedIncomeType.current = watchedIncomeType;
    onIncomeTypeChange(watchedIncomeType);
  }, [watchedIncomeType, onIncomeTypeChange, incomeTypes]);

  useEffect(() => {
    const parsed = parseAmountFromForm(watchedAmount);
    if (parsed === lastSyncedAmount.current) return;
    // Only persist when the user has finished typing (i.e. amount is a number).
    // While the input is focused the field value is still a string like "$1,2".
    if (typeof watchedAmount !== "number") return;
    lastSyncedAmount.current = parsed;
    onAmountChange(parsed);
  }, [watchedAmount, onAmountChange]);

  return (
    <Card>
      <div className={styles.additionalCard}>
        <div className={styles.additionalHeader}>
          <span className="fontWeight--bold">
            {l10n.getString("additional-income-heading", null, "Additional income source")}
          </span>
          <IconButton
            label={l10n.getString("additional-income-remove", null, "Remove income source")}
            kind="plain"
            name="trash-2"
            size="s"
            onClick={(event: React.MouseEvent) => {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
            }}
          />
        </div>

        <div className={styles.additionalFields}>
          <Combobox
            control={control}
            name="additional_income_type"
            label={l10n.getString("additional-income-type", null, "Income type")}
            items={items}
          />
          <AnnualMonthlyIncome control={control} />
        </div>
      </div>
    </Card>
  );
}

function EmploymentDetails({ applicantType = "primary" }: { applicantType?: ApplicantType }) {
  const { l10n } = useLocalization();
  const { showGenericErrorToast } = useToastsContext();
  const { goBack, goNext, startOver } = useFlowNavigation();
  const { showEmploymentDetailsStep } = useLendingApplicationSteps();
  const { coApplicant, isLoading: isCoApplicantLoading } = useGetCoApplicantQuery();
  const { institution } = useGetInstitutionQuery();
  const { data: storeData } = useStore();
  const lastInitializedAmount = useRef<number | null>(null);

  const maxAdditionalSources = institution.lending_additional_income_max_sources ?? 0;
  const additionalIncomeTypes: string[] = institution.lending_additional_income_types ?? [];
  const isAdditionalIncomeSourcesFeatureEnabled =
    institution.lending_ask_additional_fields?.some(
      (field) => field.name === "additional_income_sources" && field.value
    ) ?? false;
  const additionalIncomeEnabled =
    isAdditionalIncomeSourcesFeatureEnabled &&
    maxAdditionalSources > 0 &&
    additionalIncomeTypes.length > 0;

  const { data: incomeDetailsResponse } = useListApplicantIncomeDetailsQuery();
  const incomeDetailsList = incomeDetailsResponse?.results ?? [];
  const primaryIncome = getPrimaryIncome({
    applicantType,
    incomeDetailsList,
    coApplicantUuid: coApplicant?.id,
  });
  const additionalSources = getAdditionalIncomes({
    applicantType,
    incomeDetailsList,
    coApplicantUuid: coApplicant?.id,
  });
  const primaryIncomeUuid =
    primaryIncome?.id ??
    (applicantType === "co-applicant"
      ? storeData.coApplicantIncomeDetailsId
      : storeData.incomeDetailsId);
  const applicantDetailsUuid =
    primaryIncome?.lending_application_applicant_details ??
    (applicantType === "co-applicant" ? coApplicant?.id : undefined);

  const coApplicantFirstName = coApplicant?.first_name?.trim();
  const coApplicantName =
    coApplicantFirstName && coApplicantFirstName.length > 0
      ? coApplicantFirstName
      : l10n.getString("co-applicant-name-fallback", null, "your co-borrower");

  const heading =
    applicantType === "co-applicant"
      ? l10n.getString(
          "co-applicant-income-heading",
          { name: coApplicantName },
          `Tell us about ${coApplicantName}'s income`
        )
      : l10n.getString("income-heading", null, "What's your income?");

  const subheading =
    applicantType === "co-applicant"
      ? l10n.getString(
          "co-applicant-income-subheading",
          null,
          "Tell us how much they make in a year, before taxes. You can include wages, retirement, investments, and all other income. An estimate is just fine."
        )
      : l10n.getString(
          "income-subheading",
          null,
          "Tell us approximately how much you make in a year, before taxes. You can include wages, retirement, investments, and all other income."
        );

  const { handleSubmit, control, reset } = useForm<FormData>({
    defaultValues: {
      amount: undefined,
    },
    resolver: zodResolver(createSchema(l10n)),
  });

  useEffect(() => {
    if (!showEmploymentDetailsStep) {
      goNext();
    }
  }, [goNext, showEmploymentDetailsStep]);

  useEffect(() => {
    if (primaryIncome?.amount == null) {
      return;
    }

    if (lastInitializedAmount.current === primaryIncome.amount) {
      return;
    }

    lastInitializedAmount.current = primaryIncome.amount;
    reset({ amount: primaryIncome.amount });
  }, [primaryIncome?.amount, reset]);

  // These saves are awaited so we can sequence work after them (navigate, or nothing);
  // each caller owns its failure in the catch, so the hooks don't need an onError.
  const { mutateAsync: updateIncomeDetails, isPending } = usePatchApplicantIncomeDetailsQuery();
  const { mutateAsync: createIncomeDetails, isPending: isCreatingIncomeDetails } =
    useCreateApplicantIncomeDetailsQuery();
  const { mutateAsync: patchIncomeDetails } = usePatchApplicantIncomeDetailsQuery();
  const { mutateAsync: deleteIncomeDetails } = useDeleteApplicantIncomeDetailsQuery();

  async function handleAddSource() {
    // Defense-in-depth against double-clicks — the button is also disabled while
    // the request is in flight.
    if (isCreatingIncomeDetails) return;
    if (!applicantDetailsUuid) {
      showGenericErrorToast();
      return;
    }
    const defaultType = additionalIncomeTypes[0];
    if (!defaultType) return;

    try {
      await createIncomeDetails({
        // @ts-expect-error OpenAPI marks `id` as required on create.
        body: {
          lending_application_applicant_details: applicantDetailsUuid,
          income_type: "other",
          additional_income_type: defaultType,
          amount: 0,
          is_primary: false,
        },
      });
    } catch {
      showGenericErrorToast();
    }
  }

  async function handlePatchSource(
    uuid: string,
    patch: { additional_income_type?: string; amount?: number }
  ) {
    try {
      await patchIncomeDetails({
        params: { path: { uuid } },
        body: patch,
      });
    } catch {
      showGenericErrorToast();
    }
  }

  async function handleDeleteSource(uuid: string) {
    try {
      await deleteIncomeDetails({ params: { path: { uuid } } });
    } catch {
      showGenericErrorToast();
    }
  }

  async function onSubmit(formData: FormData) {
    if (!primaryIncomeUuid) {
      showGenericErrorToast();
      startOver();
      return;
    }

    try {
      await updateIncomeDetails({
        params: { path: { uuid: primaryIncomeUuid } },
        body: {
          amount: formData.amount,
        },
      });
      goNext();
    } catch {
      // Stay on the page; the user can retry.
      showGenericErrorToast();
    }
  }

  if (!showEmploymentDetailsStep) {
    return null;
  }

  const summary = primaryIncome ? formatEmploymentSummary(primaryIncome, l10n) : null;
  const canAddSource = additionalIncomeEnabled && additionalSources.length < maxAdditionalSources;

  return (
    <PageLayout isLoading={isCoApplicantLoading}>
      <PageContent>
        <PageHeader heading={heading} subheading={subheading} />
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className={styles.primaryCard}>
            <Card>
              {summary && (
                <div className={styles.summary}>
                  <div className={styles.summaryIcon} aria-hidden="true">
                    <span className="narmi-icon-refresh-ccw" />
                  </div>
                  <div>
                    <div className="fontWeight--bold">{summary.heading}</div>
                    {summary.detail && <div className={styles.summaryDetail}>{summary.detail}</div>}
                  </div>
                </div>
              )}
              <AnnualMonthlyIncome control={control} />
            </Card>
          </div>

          {additionalIncomeEnabled && applicantDetailsUuid && (
            <div className={styles.additionalSources}>
              {additionalSources.map((source) => (
                <AdditionalIncomeCard
                  key={source.id}
                  source={source}
                  incomeTypes={additionalIncomeTypes}
                  onIncomeTypeChange={(nextType) =>
                    handlePatchSource(source.id as string, { additional_income_type: nextType })
                  }
                  onAmountChange={(nextAmount) =>
                    handlePatchSource(source.id as string, { amount: nextAmount })
                  }
                  onRemove={() => handleDeleteSource(source.id as string)}
                />
              ))}

              {canAddSource && (
                <div className={styles.addSourceRow}>
                  <PlainButton
                    label={l10n.getString("add-income-source", null, "+ Add income source")}
                    disabled={isCreatingIncomeDetails}
                    onClick={handleAddSource}
                  />
                </div>
              )}
            </div>
          )}

          <div className={styles.actions}>
            <Row alignItems="center" justifyContent="space-between">
              <BackButton onClick={goBack} />
              <Button
                label={l10n.getString("button-label-next", null, "Next")}
                type="submit"
                isLoading={isPending}
              />
            </Row>
          </div>
        </form>
      </PageContent>
    </PageLayout>
  );
}

export { EmploymentDetails };
